import axios from 'axios';
import { apiRoute } from '../constants/api-constants';
import {
  PassAction,
  PassShareCreated,
  PassSpecInput,
  PassSuggestion,
  ResidentPass,
  ResidentPassDetail,
  ResidentPassList,
} from '../interfaces/visitor-pass';

/**
 * THE RESIDENT'S OWN VISITOR PASSES (resident-portal Plans 3 and 4).
 *
 * Every route here is bounded server-side to the household the caller lives in,
 * through the lb_my_pass_* SECURITY DEFINER functions (migration 119). Like
 * tenant-client.ts, nothing here takes a unit, building or person: the server
 * reads the subject from the session — except which home a new pass is for,
 * when the resident has more than one (host_occupancy_id, one of their own).
 *
 * Every write answers with the pass as it now stands (data.pass, the same shape
 * as the detail read), so a screen can redraw from it without a re-read.
 *
 * The default axios instance, so every call carries the session cookies and the
 * CSRF header from axios-setup.ts.
 */

export const listPasses = async (): Promise<ResidentPassList> => {
  const { data } = await axios.get(`${apiRoute}/tenant/passes`);

  return {
    homes: Array.isArray(data?.homes) ? data.homes : [],
    passes: Array.isArray(data?.passes) ? data.passes : [],
    limits: data?.limits,
  } as ResidentPassList;
};

export const getPass = async (passId: string): Promise<ResidentPassDetail> => {
  const { data } = await axios.get(
    `${apiRoute}/tenant/passes/${encodeURIComponent(passId)}`
  );

  return {
    pass: data?.pass,
    visits: Array.isArray(data?.visits) ? data.visits : [],
    limits: data?.limits,
  } as ResidentPassDetail;
};

/** The editor's body, plus the home when the resident has more than one. */
export type ResidentPassCreate = PassSpecInput & { host_occupancy_id?: string };

export const createPass = async (body: ResidentPassCreate): Promise<ResidentPass> => {
  const { data } = await axios.post(`${apiRoute}/tenant/passes`, body);

  return data?.pass as ResidentPass;
};

/** Any subset of the spec; never the host (a pass does not change homes). */
export const updatePass = async (
  passId: string,
  body: Partial<PassSpecInput>
): Promise<ResidentPass> => {
  const { data } = await axios.patch(
    `${apiRoute}/tenant/passes/${encodeURIComponent(passId)}`,
    body
  );

  return data?.pass as ResidentPass;
};

/**
 * Pause (no date = until further notice), resume, end ('revoke'), and skip /
 * unskip one visit — those two need the visit's date, 'YYYY-MM-DD' in the
 * building's zone.
 */
export const passAction = async (
  passId: string,
  action: PassAction,
  date?: string
): Promise<ResidentPass> => {
  const { data } = await axios.post(
    `${apiRoute}/tenant/passes/${encodeURIComponent(passId)}/actions`,
    date ? { action, date } : { action }
  );

  return data?.pass as ResidentPass;
};

/**
 * "Lobbero remembers" (Plan 5): visitors who keep coming on the same weekdays,
 * at most three per home. An empty list is the normal answer.
 */
export const listSuggestions = async (): Promise<PassSuggestion[]> => {
  const { data } = await axios.get(`${apiRoute}/tenant/passes/suggestions`);

  return Array.isArray(data?.suggestions) ? (data.suggestions as PassSuggestion[]) : [];
};

/** "Not now" — hidden for the whole household, for that home. */
export const dismissSuggestion = async (
  suggestion: Pick<PassSuggestion, 'key_token' | 'occupancy_id'>
): Promise<void> => {
  await axios.post(`${apiRoute}/tenant/passes/suggestions/dismiss`, {
    key_token: suggestion.key_token,
    occupancy_id: suggestion.occupancy_id,
  });
};

/**
 * SHARE A PASS AS A LINK (Plan 6, RP-6.2). The answer carries the link ONCE —
 * only its fingerprint is kept server-side, so it can never be read back. A
 * second share makes a new link and the old one stops working.
 */
export const sharePass = async (passId: string): Promise<PassShareCreated> => {
  const { data } = await axios.post(
    `${apiRoute}/tenant/passes/${encodeURIComponent(passId)}/link`,
    {}
  );

  return {
    url: typeof data?.url === 'string' ? data.url : '',
    link_id: data?.link_id,
    pass: (data?.pass as ResidentPass) || null,
  };
};

/** Stop sharing: the link stops working at once. Safe to repeat. */
export const stopSharing = async (passId: string): Promise<ResidentPass | null> => {
  const { data } = await axios.delete(
    `${apiRoute}/tenant/passes/${encodeURIComponent(passId)}/link`
  );

  return (data?.pass as ResidentPass) || null;
};

/**
 * THE AI QUICK-ADD FALLBACK (resident-portal Plan 5, RP-5.8). Off unless the
 * organisation has Lobbero IQ and the building team switched it on; the answer
 * says whether to offer it at all. Never throws: "not available" is the answer
 * to every failure.
 */
export const parseAvailability = async (): Promise<boolean> => {
  try {
    const { data } = await axios.get(`${apiRoute}/tenant/passes/parse`);

    return data?.available === true;
  } catch {
    return false;
  }
};

/**
 * The model's reading of the sentence's days, times, dates and who — never a
 * name (the server masks every word that is not about time before the model
 * sees it). `names` are the name words the local parser found, so the server
 * can mask them precisely. Resolves to the fields to fill ({} for nothing).
 */
export const parsePass = async (
  text: string,
  names: string[],
  /** The home the pass is for. A resident with two homes in two zones is on two
   *  clocks; the server reads the sentence's days on THIS home's. Omitted when
   *  the form does not know, and the server then uses the primary home. */
  hostOccupancyId?: string
): Promise<Partial<PassSpecInput>> => {
  const { data } = await axios.post(`${apiRoute}/tenant/passes/parse`, {
    text,
    names,
    ...(hostOccupancyId ? { host_occupancy_id: hostOccupancyId } : {}),
  });

  return data && typeof data.fields === 'object' && data.fields ? (data.fields as Partial<PassSpecInput>) : {};
};
